import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Card, CardContent, Container, Dialog, 
  DialogActions, DialogContent, DialogTitle, Divider, 
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, 
  TextField, Typography, IconButton 
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import SupabaseService from '../supabase/supabaseService';

const MealCalendar = () => {
  const { user } = useSupabaseAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [meals, setMeals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [mealData, setMealData] = useState({
    name: '',
    type: 'breakfast',
    notes: ''
  });

  // Psychology-informed color palette
  const colors = {
    primary: '#9c27b0', // Purple - creativity, wisdom
    secondary: '#ff9800', // Orange - energy, stimulates appetite
    background: '#f8f5fd', // Light purple-white - clean, relaxing
    accent: '#4caf50', // Green - health, freshness
    mealTypeColors: {
      breakfast: '#42a5f5', // Blue - morning, fresh start
      lunch: '#ff9800',     // Orange - middle of day, energy
      dinner: '#7e57c2',    // Purple - evening, relaxation
      snack: '#66bb6a'      // Green - health, small bites
    }
  };

  // Fetch meals for the selected date when date changes or user logs in
  useEffect(() => {
    if (user) {
      fetchMealsForDate(selectedDate);
    } else {
      setMeals([]);
    }
  }, [selectedDate, user]);

  // Convert date to string format for Supabase queries
  const dateToString = (date) => {
    return date.format('YYYY-MM-DD');
  };

  // Fetch meals for the selected date from Supabase
  const fetchMealsForDate = async (date) => {
    if (!user) return;
    
    const dateStr = dateToString(date);
    
    try {
      const meals = await SupabaseService.queryRecords('meal_calendar', {
        filters: {
          user_id: user.id,
          date: dateStr
        },
        order: [{ column: 'type', ascending: true }]
      });
      
      setMeals(meals);
    } catch (error) {
      console.error('Error fetching meals:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleOpenDialog = (meal = null) => {
    if (meal) {
      setEditingMeal(meal);
      setMealData({
        name: meal.name,
        type: meal.type,
        notes: meal.notes || ''
      });
    } else {
      setEditingMeal(null);
      setMealData({
        name: '',
        type: 'breakfast',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMealData({
      ...mealData,
      [name]: value
    });
  };

  const handleSaveMeal = async () => {
    if (!user || !mealData.name) return;
    
    const dateStr = dateToString(selectedDate);
    
    try {
      if (editingMeal) {
        // Update existing meal
        await SupabaseService.updateRecord('meal_calendar', editingMeal.id, {
          name: mealData.name,
          type: mealData.type,
          notes: mealData.notes
        });
      } else {
        // Add new meal
        await SupabaseService.addRecord('meal_calendar', {
          date: dateStr,
          name: mealData.name,
          type: mealData.type,
          notes: mealData.notes
        });
      }
      
      // Refresh meals for the selected date
      fetchMealsForDate(selectedDate);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving meal:', error);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    if (!user || !mealId) return;
    
    try {
      await SupabaseService.deleteRecord('meal_calendar', mealId);
      // Refresh meals for the selected date
      fetchMealsForDate(selectedDate);
    } catch (error) {
      console.error('Error deleting meal:', error);
    }
  };

  // Group meals by type
  const mealsByType = {
    breakfast: meals.filter(meal => meal.type === 'breakfast'),
    lunch: meals.filter(meal => meal.type === 'lunch'),
    dinner: meals.filter(meal => meal.type === 'dinner'),
    snack: meals.filter(meal => meal.type === 'snack')
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          background: colors.background,
          position: 'relative'
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ 
          color: colors.primary, 
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CalendarMonthIcon fontSize="large" />
          Meal Calendar
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ mb: 4 }}>
          Plan and organize your meals throughout the week. Click on a date to view or add meals for that day.
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar 
                value={selectedDate}
                onChange={handleDateChange}
                sx={{
                  width: '100%',
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
                  '& .MuiPickersDay-root.Mui-selected': {
                    bgcolor: colors.primary,
                  }
                }}
              />
            </LocalizationProvider>
            
            {user && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button 
                  variant="contained"
                  onClick={() => handleOpenDialog()} 
                  startIcon={<AddIcon />}
                  sx={{
                    bgcolor: colors.primary,
                    '&:hover': {
                      bgcolor: '#7b1fa2'
                    },
                    borderRadius: 8,
                    px: 3
                  }}
                >
                  Add Meal
                </Button>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} md={7}>
            <Card 
              sx={{ 
                height: '100%',
                boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
                borderRadius: 2 
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: colors.primary }}>
                  Meals for {selectedDate.format('MMMM D, YYYY')}
                </Typography>
                
                <Divider sx={{ mb: 3 }} />
                
                {!user ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Please log in to manage your meal calendar
                    </Typography>
                  </Box>
                ) : meals.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No meals planned for this day. Click "Add Meal" to create one.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {Object.entries(mealsByType).map(([type, typeMeals]) => (
                      <Grid item xs={12} key={type}>
                        {typeMeals.length > 0 && (
                          <Box>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: colors.mealTypeColors[type],
                                textTransform: 'capitalize',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              {type}
                            </Typography>
                            
                            {typeMeals.map((meal) => (
                              <Paper 
                                key={meal.id} 
                                elevation={0}
                                sx={{ 
                                  p: 2,
                                  mb: 1.5,
                                  borderLeft: `4px solid ${colors.mealTypeColors[type]}`,
                                  bgcolor: `${colors.mealTypeColors[type]}10`,
                                  position: 'relative'
                                }}
                              >
                                <Typography variant="body1" fontWeight="medium">
                                  {meal.name}
                                </Typography>
                                
                                {meal.notes && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {meal.notes}
                                  </Typography>
                                )}
                                
                                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleOpenDialog(meal)}
                                    sx={{ color: colors.primary }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteMeal(meal.id)}
                                    sx={{ color: '#f44336' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Add/Edit Meal Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: colors.primary, color: 'white' }}>
          {editingMeal ? 'Edit Meal' : 'Add New Meal'}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Meal Name"
                fullWidth
                value={mealData.name}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Meal Type</InputLabel>
                <Select
                  name="type"
                  value={mealData.type}
                  onChange={handleInputChange}
                  label="Meal Type"
                >
                  <MenuItem value="breakfast">Breakfast</MenuItem>
                  <MenuItem value="lunch">Lunch</MenuItem>
                  <MenuItem value="dinner">Dinner</MenuItem>
                  <MenuItem value="snack">Snack</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes (Optional)"
                fullWidth
                multiline
                rows={3}
                value={mealData.notes}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Add recipe details, ingredients, or other notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSaveMeal} 
            variant="contained" 
            disabled={!mealData.name}
            sx={{ bgcolor: colors.primary }}
          >
            {editingMeal ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MealCalendar;
